<template>
  <div class="admin-layout">
    <!-- Initial Loading State - Show while auth is initializing -->
    <div v-if="isInitialLoad" class="initial-loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner">
          <div class="spinner-ring"></div>
          <div class="spinner-ring spinner-ring-active"></div>
        </div>
        <div class="loading-text">
          <h3>{{ $t('admin.layout.loadingDashboard') }}</h3>
          <p>{{ $t('admin.layout.verifyingSession') }}</p>
        </div>
      </div>
    </div>

    <!-- Main Admin Layout - Only render when auth is ready -->
    <template v-else>
      <!-- Sidebar -->
      <AdminSidebar />

      <!-- Header -->
      <AdminHeader />

      <!-- Main Content Area -->
      <main class="main-content">
        <div class="content-wrapper">
          <!-- Page Header Slot -->
          <div v-if="$slots.header" class="page-header-slot">
            <slot name="header" />
          </div>

          <!-- Page Content -->
          <div class="page-content">
            <slot />
          </div>
        </div>
      </main>

      <!-- Global Loading Overlay (for operations, not initial load) -->
      <div v-if="adminStore.isLoading && !isInitialLoad" class="loading-overlay">
        <div class="loading-content">
          <div class="loading-spinner">
            <div class="spinner-ring"></div>
            <div class="spinner-ring spinner-ring-active"></div>
          </div>
          <div class="loading-text">
            <h3>{{ $t('admin.states.loading') }}</h3>
            <p>{{ $t('admin.layout.fetchingData') }}</p>
          </div>
        </div>
      </div>

      <!-- Mobile Sidebar Overlay -->
      <div
        v-if="adminStore.sidebarMobileOpen"
        class="mobile-overlay"
        @click="adminStore.toggleMobileSidebar()"
      ></div>

      <!-- Security Notifications -->
      <SecurityNotificationContainer />
    </template>
  </div>
</template>

<script setup>
const adminStore = useAdminStore()
const { setupActivityRefresh } = useHttpOnlyAuth()

// Track initial load state separately
const isInitialLoad = ref(true)

onMounted(async () => {
  try {
    // Only initialize if not already authenticated
    if (!adminStore.isAuthenticated) {
      await adminStore.fetchUser()
    }
    adminStore.initializeSidebarState()

    // Setup automatic session refresh on activity
    const cleanup = setupActivityRefresh()

    // Cleanup on unmount
    onUnmounted(() => {
      if (cleanup) cleanup()
    })
  } catch (error) {
    console.error('Failed to initialize admin layout:', error)
  } finally {
    // Small delay to ensure Vue has time to update reactive state
    await nextTick()
    isInitialLoad.value = false
  }
})
</script>

<style scoped>
.admin-layout {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  padding-top: 64px;
  transition: margin-left 0.3s ease;
  background: #f8fafc;
  min-height: calc(100vh - 64px);
  overflow-x: auto;
  width: 100%;
  position: relative;
}

/* Desktop: Use dynamic sidebar width */
@media (min-width: 1024px) {
  .main-content {
    margin-left: v-bind('adminStore.sidebarWidth');
    width: calc(100vw - v-bind('adminStore.sidebarWidth'));
    max-width: calc(100vw - v-bind('adminStore.sidebarWidth'));
  }
}

/* Mobile: No margin */
@media (max-width: 1023px) {
  .main-content {
    margin-left: 0 !important;
    width: 100vw;
    max-width: 100vw;
  }

  .admin-layout {
    flex-direction: column;
  }
}

.content-wrapper {
  padding: 1rem;
  max-width: 100%;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

@media (min-width: 640px) {
  .content-wrapper {
    padding: 1.5rem;
  }
}

@media (min-width: 1024px) {
  .content-wrapper {
    padding: 2rem;
  }
}

.page-header-slot {
  margin-bottom: 2rem;
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* Loading Overlay */
.loading-overlay,
.initial-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.initial-loading-overlay {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.loading-content {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 400px;
  width: 90%;
}

.loading-spinner {
  position: relative;
  width: 48px;
  height: 48px;
}

.spinner-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.spinner-ring-active {
  border-color: #3b82f6;
  border-top-color: transparent;
}

.loading-text h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem 0;
}

.loading-text p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Mobile Overlay */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 45;
}

@media (min-width: 1024px) {
  .mobile-overlay {
    display: none;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
